import { SubjectData, noop } from '@aloft/toolkit-shared';
import { Flex, IconButton, Switch, Typography } from '@aloft/toolkit-sui';

import { FieldPath, useWatch } from 'react-hook-form';

import {
  selectDefaultFeatureAreas,
  selectHasArvOption,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectAivArvActiveTab } from '../../../redux/features/selectors/uiSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { PROPERTY_GRID_ROW_HEIGHT } from '../consts.ts';
import { PropertyGridForm, PropertyGridSectionProps } from '../types.ts';
import { handleUpdatePropertyData } from '../utils.ts';
import {
  applyEstimatedFeatureArea,
  getGridSourceFeatureAreas,
} from '../utils/applyEstimatedFeatureArea.ts';

type TitleRowProps = Pick<
  PropertyGridSectionProps,
  'columnIndex' | 'feature' | 'fieldTypes' | 'isRemovable' | 'onRemove' | 'control' | 'getValues'
> & {
  title: string;
};

const MarkFeatureAsNoneComponent = ({
  columnIndex,
  title,
  feature,
  control,
  getValues,
}: TitleRowProps) => {
  const dispatch = useAppDispatch();
  const hasArvOption = useAppSelector(selectHasArvOption);
  const activeTab = useAppSelector(selectAivArvActiveTab);
  const defaultFeatureAreas = useAppSelector(selectDefaultFeatureAreas);
  const column = useWatch({ control, name: `columns.${columnIndex}` });
  const { propertyData, propertyId, type } = column ?? {};
  const hasFeatureKey = feature === 'manualCost' ? 'hasManualCost' : `${feature}.hasFeature`;
  const hasFeature = useWatch({
    control,
    name: `columns.${columnIndex}.propertyData.editableData.improvements.${hasFeatureKey}` as FieldPath<PropertyGridForm>,
  }) as boolean;
  const hasArvFeature = useWatch({
    control,
    name: `columns.${columnIndex}.propertyData.editableData.arv.improvements.${hasFeatureKey}` as FieldPath<PropertyGridForm>,
  }) as boolean;
  if (!propertyData || !propertyId || !type) return null;

  let hasFeatureValue = hasFeature;
  if (hasArvOption && propertyId === 'subject') {
    if (activeTab === 'all') {
      hasFeatureValue = hasFeature || hasArvFeature;
    } else if (activeTab === 'arv') {
      hasFeatureValue = hasArvFeature;
    }
  }

  const handleSwitchChange = (value: boolean) => {
    if (!propertyData || !feature) return;

    if (feature === 'manualCost') {
      if (propertyId !== 'subject' || !hasArvOption || activeTab === 'all' || activeTab === 'aiv') {
        propertyData.editableData.improvements.hasManualCost = !value;
      }
      if (
        propertyId === 'subject' &&
        hasArvOption &&
        (activeTab === 'all' ||
          (activeTab === 'arv' && (propertyData.editableData as SubjectData['editableData']).arv))
      ) {
        (propertyData.editableData as SubjectData['editableData']).arv!.improvements.hasManualCost =
          !value;
      }
    } else if (feature !== 'other') {
      const enabling = !value;

      if (
        (propertyId !== 'subject' || !hasArvOption || activeTab === 'all' || activeTab === 'aiv') &&
        propertyData.editableData.improvements
      ) {
        let improvements = {
          ...propertyData.editableData.improvements,
          [feature]: {
            ...propertyData.editableData.improvements[feature],
            hasFeature: enabling,
          },
        };
        let selectedSources = propertyData.selectedSources?.editableData?.improvements;

        if (enabling) {
          ({ improvements, selectedSources } = applyEstimatedFeatureArea({
            improvements,
            selectedSources,
            feature,
            defaultAreas: defaultFeatureAreas,
            sourceAreas: getGridSourceFeatureAreas(getValues('columns'), propertyId, feature),
          }));
        }

        propertyData.editableData.improvements = improvements;
        if (selectedSources) {
          propertyData.selectedSources = {
            ...propertyData.selectedSources,
            editableData: {
              ...propertyData.selectedSources?.editableData,
              improvements: selectedSources,
            },
          };
        }
      }

      if (
        propertyId === 'subject' &&
        hasArvOption &&
        (activeTab === 'all' || activeTab === 'arv') &&
        (propertyData.editableData as SubjectData['editableData']).arv?.improvements
      ) {
        const arvEditable = (propertyData.editableData as SubjectData['editableData']).arv!;
        let improvements = {
          ...arvEditable.improvements,
          [feature]: {
            ...arvEditable.improvements[feature],
            hasFeature: enabling,
          },
        };
        let selectedSources =
          propertyData.selectedSources?.editableData?.arv?.improvements ??
          propertyData.selectedSources?.editableData?.improvements;

        if (enabling) {
          ({ improvements, selectedSources } = applyEstimatedFeatureArea({
            improvements,
            selectedSources,
            feature,
            defaultAreas: defaultFeatureAreas,
            sourceAreas: getGridSourceFeatureAreas(getValues('columns'), propertyId, feature),
          }));
        }

        (propertyData.editableData as SubjectData['editableData']).arv = {
          ...arvEditable,
          improvements,
        };
        if (selectedSources) {
          propertyData.selectedSources = {
            ...propertyData.selectedSources,
            editableData: {
              ...propertyData.selectedSources?.editableData,
              arv: {
                ...propertyData.selectedSources?.editableData?.arv,
                improvements: selectedSources,
              },
            },
          };
        }
      }
    }

    handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
  };

  if (type === 'property' && feature) {
    return (
      <Flex>
        <Switch
          id={`no-${title}-${columnIndex}`}
          label="None"
          value={!hasFeatureValue}
          onChange={handleSwitchChange}
        />
      </Flex>
    );
  }

  return null;
};

const RemoveFeatureSectionComponent = ({
  columnIndex,
  onRemove = noop,
  control,
}: TitleRowProps) => {
  const column = useWatch({ control, name: `columns.${columnIndex}` });
  const { propertyData, propertyId, type } = column ?? {};

  if (!propertyData || !propertyId || !type || type !== 'property') return null;

  if (propertyId === 'subject') {
    return (
      <IconButton
        __dangerDoNotUseSx={{ marginLeft: '-10px' }}
        ariaLabel="Remove feature"
        title="Remove feature"
        icon="remove"
        iconColor="textError"
        variant="tertiary"
        onClick={onRemove}
      />
    );
  }

  return null;
};

const HasFeatureSectionComponent = ({ columnIndex, control, feature }: TitleRowProps) => {
  const column = useWatch({ control, name: `columns.${columnIndex}` });
  const { propertyData, propertyId, type } = column ?? {};

  const hasFeatureKey = feature === 'manualCost' ? 'hasManualCost' : `${feature}.hasFeature`;
  const hasFeature = useWatch({
    control,
    name: `columns.${columnIndex}.propertyData.editableData.improvements.${hasFeatureKey}` as FieldPath<PropertyGridForm>,
  }) as boolean;

  if (
    !propertyData ||
    !propertyId ||
    !type ||
    type === 'property' ||
    feature === 'manualCost' ||
    hasFeature === undefined
  )
    return null;

  return (
    <Typography variant="h4" color={hasFeature ? 'textPrimary' : 'textSecondary'}>
      {hasFeature ? 'Yes' : 'None'}
    </Typography>
  );
};

export const TitleRow = (props: TitleRowProps) => {
  const { fieldTypes, title, isRemovable } = props;

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      backgroundColor="surface4"
      height={PROPERTY_GRID_ROW_HEIGHT}
      padding="8px"
      gridColumn="1 / -1"
      width="100%"
      overflow="hidden"
    >
      <Flex alignItems="center">
        {isRemovable && <RemoveFeatureSectionComponent {...props} />}
        {fieldTypes.includes('label') && (
          <Typography variant="h3" truncate __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}>
            {title}
          </Typography>
        )}
      </Flex>
      <HasFeatureSectionComponent {...props} />
      <MarkFeatureAsNoneComponent {...props} />
    </Flex>
  );
};
